import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HeaderComponent } from '@app/shared/components/header/header.component';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { NumericTextBoxComponent, SwitchComponent } from '@progress/kendo-angular-inputs';
import { ProjectModulePipeline } from '../../models/project-module-pipeline';
import { ProjectModuleService } from '../../services/project-module.service';

@Component({
  selector: 'app-pipeline',
  templateUrl: './pipeline.component.html',
  styleUrls: ['./pipeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ComboBoxComponent,
    FormsModule,
    HeaderComponent,
    NumericTextBoxComponent,
    ReactiveFormsModule,
    SwitchComponent,
  ],
})

export class PipelineComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private _projectModuleService = inject(ProjectModuleService);

  readonly xProjectId = input<number>(undefined);

  // emitter tabTdv provides touched-dirty-valid status of the form in this tab
  readonly tabTdv = output<boolean>();

  private thisProjectId;
  private thisProjectModuleId: number;
  public decimalPrecision: number = 0;
  public decimalFormat: string = 'n0';
  public percentFormat: string = 'p0';

  private projectModuleTableName: string = 'ProjectModulePipelines';

  public errorMessage: string;
  public isFormNew: boolean;
  public isProjectModuleReadError: boolean = false;

  public projectModuleForm: UntypedFormGroup;
  public projectModule: ProjectModulePipeline;
  public projectModuleCount: number;
  public xModule: ProjectModulePipeline;
  public yModule: ProjectModulePipeline;
  public newModule: ProjectModulePipeline;
  public modifyModule: ProjectModulePipeline;

  public selectMaterial: Array<string> = ['Steel', 'Iron', 'PVC', 'Aluminum'];
  public selectDiameter: Array<number> = [24, 32, 38, 45];
  public selectRating: Array<number> = [1200, 1800, 2200, 3000, 4000];

  ngOnInit() {
    this.generateForm();
    this.thisProjectId = this.xProjectId();
    this.getProjectModule(this.thisProjectId);
  }

  public getProjectModule(v: number): void {
    this._projectModuleService.get(v, this.projectModuleTableName)
      .then(payload => {
        this.projectModule = payload.value;
        this.projectModuleCount = payload['@odata.count'];
        if (this.projectModuleCount === 0) {
          this.isFormNew = true;
        } else {
          if (this.projectModuleCount === 1) {
            this.isFormNew = false;
            this.thisProjectModuleId = this.projectModule[0].ProjectModulePipelineId;
            this.populateForm(payload.value[0]);
          } else {
            this.isProjectModuleReadError = true;
          }
        }
      });
  }

  private populateForm(data): void {
    this.projectModuleForm.patchValue({
      PipeMaterial: data.PipeMaterial,
      PipeNominalDiameter: data.PipeNominalDiameter,
      PressureRating: data.PressureRating,
      GisEstimatedPipeLength: data.GisEstimatedGasLiftLineLength,
      PipelineLength: data.PipelineLength,
      BoreLength: data.BoreLength,
      LineCrossings: data.LineCrossings,
      RockPercent: data.RockPercent,
      IsComingled: data.IsComingled,
      IsPiggingLine: data.IsPiggingLine
    });
  }

  // build the form using FormBuilder
  private generateForm(): void {
    this.projectModuleForm = this.fb.group({
      Description: [null],
      PipeMaterial: [null, [Validators.required]],
      PipeNominalDiameter: [null, [Validators.required]],
      PressureRating: [null, [Validators.required]],
      PressureRatingUnitOfMeasure: [null],
      GisEstimatedPipeLength: [null, [Validators.required]],
      PipelineLength: [null, [Validators.required]],
      BoreLength: [null, [Validators.required]],
      LineCrossings: [null, [Validators.required]],
      RockPercent: [null, [Validators.required]],
      IsComingled: [false],
      IsPiggingLine: [false]
    });

    //  Subscribe to Value Changes on the form group. Call onValueChanged method when changes occur
    this.projectModuleForm.valueChanges.subscribe(data => this.onFormValueChanged(data));
  }

  onFormValueChanged(data?: any) {
    if (!this.projectModuleForm) {
      return;
    }
    let isValid: boolean = false;
    if (this.projectModuleForm.valid) {
      isValid = true;
      this.setTDV();
    } else {
      isValid = false;
      this.clearTDV();
    }
  }

  public updateProjectModule(e) {
    this.modifyModule = {
      ProjectId: this.xProjectId(),
      Description: 'Pipeline Project Module',
      PipeMaterial: this.projectModuleForm.controls.PipeMaterial.value,
      PipeNominalDiameter: this.projectModuleForm.controls.PipeNominalDiameter.value,
      PressureRating: this.projectModuleForm.controls.PressureRating.value,
      PressureRatingUnitOfMeasure: 'psi',
      GisEstimatedGasLiftLineLength: this.projectModuleForm.controls.GisEstimatedPipeLength.value,
      PipelineLength: this.projectModuleForm.controls.PipelineLength.value,
      BoreLength: this.projectModuleForm.controls.BoreLength.value,
      LineCrossings: this.projectModuleForm.controls.LineCrossings.value,
      RockPercent: this.projectModuleForm.controls.RockPercent.value,
      IsComingled: this.projectModuleForm.controls.IsComingled.value,
      IsPiggingLine: this.projectModuleForm.controls.IsPiggingLine.value
    };
    //  Get PATCH request in form that's usable with the api
    let Form = JSON.stringify(this.modifyModule);

    this._projectModuleService.update(this.thisProjectModuleId, Form, this.projectModuleTableName)
      .then(() => {
        this.onUpdateComplete(this.thisProjectId);
      });
  }

  public createProjectModule(e): void {
    this.newModule = {
      ProjectId: this.xProjectId(),
      Description: 'Pipeline Project Module',
      PipeMaterial: this.projectModuleForm.controls.PipeMaterial.value,
      PipeNominalDiameter: this.projectModuleForm.controls.PipeNominalDiameter.value,
      PressureRating: this.projectModuleForm.controls.PressureRating.value,
      PressureRatingUnitOfMeasure: 'psi',
      GisEstimatedGasLiftLineLength: this.projectModuleForm.controls.GisEstimatedPipeLength.value,
      PipelineLength: this.projectModuleForm.controls.PipelineLength.value,
      BoreLength: this.projectModuleForm.controls.BoreLength.value,
      LineCrossings: this.projectModuleForm.controls.LineCrossings.value,
      RockPercent: this.projectModuleForm.controls.RockPercent.value,
      IsComingled: this.projectModuleForm.controls.IsComingled.value ? this.projectModuleForm.controls.IsComingled.value : false,
      IsPiggingLine: this.projectModuleForm.controls.IsPiggingLine.value ? this.projectModuleForm.controls.IsPiggingLine.value : false
    };

    //  Put POST request in form that's usable with the api
    let Form = JSON.stringify(this.newModule);

    this._projectModuleService.create(Form, this.projectModuleTableName)
      .then((data: any) => {
        this.onSaveComplete(data.ProjectId);
      });
  }

  onSaveComplete(v: number): void {
    this.isFormNew = false;
    this.getProjectModule(v);
  }

  onUpdateComplete(v: number): void {
    this.isFormNew = false;
    this.getProjectModule(v);

  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }
}